/*
  IA Wishlist: <ia-favorite>, the heart on product cards and the PDP title,
  plus the dressing of the wishlist page's own CTAs (bottom of the file).

  Swym Wishlist Plus stores the list; the theme owns the button, the click and
  the pressed state, the same split as "Save for later" in the cart
  (IaCartSfl in ia-cart.js). We drive Swym's data API directly rather than let
  its DOM binder claim the button, because the binder is gated behind the
  account's `retailerSettings.Wishlist.Enabled`, which is off on this store
  while the API itself works fine.

  - The shopper's list is fetched ONCE per page (swat.api.fetch) and shared by
    every heart, so a 40-card grid costs one request.
  - A click paints optimistically and reverts if Swym rejects or never answers.
  - variant:change repoints the PDP heart at the new variant and repaints it;
    the store saves per variant (General.ProductLevel = "product-variant").

  The connection to Swym goes through whenSwym (ia-standard-events), which
  covers a callback pushed after the app has already drained SwymCallbacks. If
  its script is blocked or the app is uninstalled nothing ever answers: the
  fetch falls back to an empty list and clicks revert, so the heart stays
  honest rather than showing a save that did not happen.
*/
import { EVENTS, subscribe } from 'ia-util-pubsub'
import { whenSwym } from 'ia-standard-events'
import { openCart } from 'ia-util-helpers'

const SWYM_WAIT_MS = 8000

let swat = null
let listPromise = null
let list = []

const withSwym = (callback) => {
  if (swat) {
    callback(swat)
    return
  }
  whenSwym((instance) => {
    swat = instance
    callback(instance)
  })
}

/* One settle path for every Swym call: its callbacks never fire when the app
   is absent, so each is raced against a timeout. */
const swymCall = (run) =>
  new Promise((resolve) => {
    let settled = false
    const settle = (value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(value)
    }
    const timer = setTimeout(() => settle(null), SWYM_WAIT_MS)
    withSwym((instance) => run(instance, settle))
  })

const getList = () => {
  if (!listPromise) {
    listPromise = swymCall((instance, settle) => {
      if (typeof instance?.api?.fetch !== 'function') {
        settle(null)
        return
      }
      instance.api.fetch(
        (items) => settle(Array.isArray(items) ? items : []),
        () => settle(null)
      )
    }).then((items) => {
      list = items || []
    })
  }
  return listPromise
}

const isSame = (item, product) =>
  String(item.empi) === String(product.empi) && String(item.epi) === String(product.epi)

class IaFavorite extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button')
    if (!this.button) return

    this.button.addEventListener('click', this.toggle.bind(this))
    this.unsubscribe = subscribe(EVENTS.variantChange, this.handleVariantChange.bind(this))
    this.repaint()
  }

  disconnectedCallback() {
    this.unsubscribe?.()
  }

  get product() {
    return {
      empi: this.dataset.productId,
      epi: this.dataset.variantId,
      du: this.dataset.productUrl
    }
  }

  async repaint() {
    await getList()
    this.paint(list.some((item) => isSame(item, this.product)))
  }

  paint(saved) {
    this.saved = saved
    this.button.setAttribute('aria-pressed', String(saved))
  }

  /* PDP only: the heart follows the size the shopper is looking at. */
  handleVariantChange(event) {
    const { productId, variant } = event.detail
    if (!variant || String(productId) !== String(this.dataset.productId)) return
    this.dataset.variantId = variant.id
    this.repaint()
  }

  async toggle() {
    if (this.pending) return
    this.pending = true

    const product = this.product
    const wanted = !this.saved
    this.paint(wanted)

    const method = wanted ? 'addToWishList' : 'removeFromWishList'
    const result = await swymCall((instance, settle) => {
      if (typeof instance?.api?.[method] !== 'function') {
        settle(null)
        return
      }
      instance.api[method](
        { empi: Number(product.empi), epi: Number(product.epi), du: product.du },
        () => settle(true),
        () => settle(null)
      )
    })

    this.pending = false
    if (!result) {
      this.paint(!wanted)
      return
    }

    // Keep the shared list in step so other hearts for this variant agree.
    list = wanted
      ? [...list, { empi: product.empi, epi: product.epi, du: product.du }]
      : list.filter((item) => !isSame(item, product))
    document.querySelectorAll('ia-favorite').forEach((heart) => {
      if (heart !== this) heart.repaint?.()
    })
  }
}

if (!customElements.get('ia-favorite')) {
  customElements.define('ia-favorite', IaFavorite)
}

/* ---- The wishlist page ----------------------------------------------------
   Swym renders the page and owns its behaviour; the theme adds two things.

   Cosmetics: every Move to Cart button is redressed into the collection
   card's reading, the label with the price and the crossed-out anchor inside
   the button. The app redraws rows freely, so a MutationObserver re-runs the
   dressing; it only writes to a button whose label is missing, which is what
   keeps its own writes from feeding it another pass.

   Cart: refresh and the drawer's opening both ride the app's own success
   hook, installed at the bottom of the file.

   Saved bundles: a record carrying cprops.iaBundleParts (written by the cart's
   Save for later) is a legacy bundle group, and Move to bag rebuilds the whole
   group instead of adding the bare parent. Intercepted in the capture phase,
   before the app's own handler.

   Go to cart (the Saved for later empty state) opens the mini cart in place:
   the theme's cart is the drawer. Intercepted the same way.
*/
const CTA_SELECTOR = '.swym-storefront-layout-grid-item-add-to-cart-button'
const GO_TO_CART_SELECTOR = '.swym-storefront-layout-empty-sfl-go-to-cart'
const CTA_LABEL = 'Move to bag'
const SETTLE_MS = 250

/* The app parks "Moving..." on a button while its own write is in flight;
   dressing it there tears up the nodes its handler still holds. */
const isBusy = (button) => button.disabled || /\u2026|\.\.\.$/.test(button.textContent.trim())

/* Read once and kept on the button: the app writes its own state into the
   price slot, so a later read would quote that back as the amount. */
const readPrice = (button, selector, memo) => {
  if (button.dataset[memo] !== undefined) return button.dataset[memo]
  const value = button
    .closest('.swym-storefront-layout-grid-item')
    ?.querySelector(selector)
    ?.textContent.trim()
  if (!value || !/\d/.test(value)) return undefined
  button.dataset[memo] = value
  return value
}

const dressCta = (button) => {
  if (isBusy(button)) return
  if (button.dataset.iaDressed !== undefined && button.textContent.includes(CTA_LABEL)) return
  const price = readPrice(button, '.swym-storefront-layout-grid-item-final-price', 'iaPrice')
  const was = readPrice(button, '.swym-storefront-layout-grid-item-compare-price', 'iaWas')
  button.textContent = CTA_LABEL
  if (price) {
    const divider = document.createElement('span')
    divider.className = 'ia-button__divider'
    divider.textContent = '-'
    const amount = document.createElement('span')
    amount.textContent = price
    button.append(divider, amount)
    // The theme's price reads sale first, the anchor crossed out beside it.
    if (was && was !== price) {
      const compare = document.createElement('strike')
      compare.textContent = was
      button.append(compare)
    }
  }
  button.dataset.iaDressed = ''
}

const wishlistPage = document.getElementById('swym-wishlist-render-container')
if (wishlistPage) {
  const dressAll = () => wishlistPage.querySelectorAll(CTA_SELECTOR).forEach(dressCta)
  dressAll()

  /* One pass per burst: the app rewrites the label, the row and sometimes the
     whole list in quick succession. */
  let settle = null
  new MutationObserver(() => {
    clearTimeout(settle)
    settle = setTimeout(dressAll, SETTLE_MS)
  }).observe(wishlistPage, { childList: true, subtree: true, characterData: true })

}

/* ---- Saved bundles ---------------------------------------------------------
   The card element holds its list record as `item`; the parts travel in
   Swym's cprops (ia-cart.js writes them on save). The group is rebuilt the
   way the PDP builders create one: the parent line carries _bundle_parent
   (its own variant), the children only the group id, which is what the
   cart's grouping keys on (ia-cart-items). The record stays in the list, exactly like the app's own
   Move to bag. */
const rebuildBundle = async (button, card) => {
  const parts = (card.item?.cprops?.iaBundleParts || '')
    .split(',')
    .map((pair) => pair.split(':').map(Number))
    .filter(([id, qty]) => id > 0 && qty > 0)
  const parentVariant = Number(card.dataset.epi)
  if (!parts.length || !parentVariant) return false

  const bundleId = `${card.dataset.empi}-${Date.now()}`
  const items = [
    { id: parentVariant, quantity: 1, properties: { _bundle_id: bundleId, _bundle_parent: String(parentVariant) } },
    ...parts.map(([id, quantity]) => ({
      id,
      quantity,
      properties: { _bundle_id: bundleId },
    })),
  ]

  button.disabled = true
  try {
    const cart = await window.iaCartAPI?.addToCartJson({ items })
    if (cart) window._SwymAJAXCart?.()
    return Boolean(cart)
  } finally {
    button.disabled = false
  }
}

if (wishlistPage) {
  wishlistPage.addEventListener(
    'click',
    (event) => {
      if (event.target.closest(GO_TO_CART_SELECTOR)) {
        event.preventDefault()
        event.stopPropagation()
        openCart()
        return
      }
      const button = event.target.closest(CTA_SELECTOR)
      if (!button) return
      const card = button.closest('swym-storefront-layout-item')
      if (!card?.item?.cprops?.iaBundleParts) return
      // Ours to answer: the app's replayAddToCart would add the bare parent.
      event.preventDefault()
      event.stopPropagation()
      if (!button.disabled) rebuildBundle(button, card)
    },
    true
  )
}

/* The app's own success hook: its add-to-cart handler ends with
   window._SwymAJAXCart() wherever it runs, page or portaled dialog. The theme
   answers it the way every ATC caller answers its own success: fresh cart,
   open drawer. Installed as a getter, because the app's bundle loads later
   and assigns its own default over a plain property; that default is built
   for Horizon's cart-drawer element and finds nothing in this theme. */
const swymCartHook = () => {
  Promise.resolve(window.iaCartAPI?.refresh?.()).finally(() => {
    openCart()
  })
}
try {
  Object.defineProperty(window, '_SwymAJAXCart', {
    configurable: true,
    get: () => swymCartHook,
    set: () => {},
  })
} catch {
  window._SwymAJAXCart = swymCartHook
}
